import { isNull } from "../../../../lib/html5-software-library/src/ranj/util/TypeUtil";
import { getURLParameter } from "../../../../lib/html5-software-library/src/ranj/util/URLUtil";
import GameEvent from "../../app/core/GameEvent";
import BaseSaveModel from "../savegame/BaseSaveModel";
import PlatformAPIEventType from "./PlatformAPIEventType";
import AppPlatformParameters from "./AppPlatformParameters";
import GlobalPlatformParameters from "./GlobalPlatformParameters";

export default class PlatformAPIService {

    /**
     * @param {BaseSaveModel} saveModel 
     * @param {soma.EventDispatcher} dispatcher
     */
    constructor(saveModel, dispatcher) {
        this._saveModel = saveModel;
        this._dispatcher = dispatcher;
        this._loggingEnabled = false;
        this._interactionEventIndex = 0;
    }

    /**
     * @param {string} version 
     */
    initialize(version) {
        this._version = version;

        this._api = discoverAPI(window);
        if (isNull(this._api)) {
            throw new Error("Platform API not found in parent or opener windows.");
        }
    }

    setApp(appId) {
        this._appId = appId;
        this._interactionEventIndex = this._saveModel.lastInteractionEventIndex;
    }

    /**
     * Enable logging of sent events in console.
     * @param {boolean} enable Whether console logs should be enabled.
     */
    enableLogging(enable) {
        this._loggingEnabled = enable;
    }

    exit() {
        this._api.exit();
    }

    supportsGetConfigurationAsync() {
        return !!this._api.getConfigurationAsync;
    }

    /**
     * Gets the global configuration from the platform API in an async manner.
     * @returns {Promise<GlobalPlatformParameters>}
     */
    async getGlobalConfigurationAsync() {
        const response = await this._api.getGlobalConfigurationAsync();
        const params = GlobalPlatformParameters.fromObject(response);
        return params;
    }

    /**
     * Gets the app specific configuration from the platform API in an async manner.
     * @returns {Promise<AppPlatformParameters>}
     */
    async getConfigurationAsync() {
        const getConfigurationAsyncIsAvailable = this.supportsGetConfigurationAsync();
        console.log("PlatformAPI.getConfigurationAsync, api method exists: " + getConfigurationAsyncIsAvailable);
        if (!getConfigurationAsyncIsAvailable) {
            throw (`PlatformAPI.getConfigurationAsync is not available`);
        }

        const response = await this._api.getConfigurationAsync(this._appId)
        const params = AppPlatformParameters.fromObject(response);
        return params;
    }

    async sendInteractionEvent(eventName, eventParams, elapsedTime) {
        const interactionEventIndex = this._interactionEventIndex;
        this._interactionEventIndex++;

        var isSuccess = await this.sendEvent(PlatformAPIEventType.INTERACTION, {
            event_name: eventName,
            event_params: eventParams,
            elapsed_time: elapsedTime,
            event_index: interactionEventIndex,
        });

        if (isSuccess) {
            // NB: it is agreed that the Platform API enforces a ordered queue sending of events
            // and if an event fails, the next ones will not be sent.
            if (this._saveModel.lastInteractionEventIndex < interactionEventIndex) {
                this._saveModel.lastInteractionEventIndex = interactionEventIndex;
            }
        }
    }

    sendProgressEvent(progressValue, elapsedTime) {
        return this.sendEvent(PlatformAPIEventType.PROGRESS, {
            progress_value: progressValue,
            elapsed_time: elapsedTime
        });
    }

    save(stateObj) {
        return this.sendEvent(PlatformAPIEventType.SAVE_REQUEST, {
            interaction_state: JSON.stringify(stateObj)
        });
    }

    /**
     * Adds missing generic parameters to payload and sends event
     * @param {string} eventType - See PlatformAPIEventType
     * @param {Object} payload 
     */
    async sendEvent(eventType, payload) {
        if (isNull(payload.appId)) {
            payload.appId = this._appId;
        }
        if (isNull(payload.timestamp)) {
            payload.timestamp = new Date().toJSON();
        }
        if (isNull(payload.version)) {
            payload.version = this._version;
        }
        if (this._loggingEnabled) {
            console.log("Calling PlatformAPI.sendEventAsync ...", eventType, payload);
        }

        let response;
        try {
            response = await this._api.sendEventAsync(eventType, payload);
        } catch (error) {
            this._handleResponseError(error);
            return false;
        }

        if (this._isResponseOk(response)) {
            if (this._loggingEnabled) {
                console.log("PlatformAPI.sendEventAsync success!", eventType, payload, response);
            }
            return true;
        } else {
            if (this._loggingEnabled) {
                console.warn("PlatformAPI.sendEventAsync failed!", eventType, payload, response);
            }
            this._handleResponseError(`PlatformAPI.sendEventAsync responded with status code: '${response?.statusCode}'`);
            return false;
        }
    }

    _applyPlatformConfiguration(platformParameters) {
        this._saveModel.setSaveGame(getURLParameter("clear") == "1" ? null : platformParameters.state);

        this._interactionEventIndex = this._saveModel.lastInteractionEventIndex;
    }

    _isResponseOk(response) {
        if (!response || !response.statusCode) {
            return false;
        }
        const statusCode = parseInt(response.statusCode);
        return statusCode >= 200 && statusCode < 300;
    }

    _handleResponseError(error) {
        console.error(`Error when sending event to Plaftorm: '${error}'. Interrupting game.`);
        // issue to Plaftorm that game needs to be interrupted
        this._api.gameInterrupted(error);
        // dispatch event that game has been interrupted
        this._dispatcher.dispatch(GameEvent.INTERRUPT_GAME, { data: new GameEvent(GameEvent.INTERRUPT_GAME) });
    }
}


/**
 * Discover API object in the opener/parent window.
 * @param {Window} win 
 * @returns 
 */
const discoverAPI = function (win) {
    var scan = function (win) {
        while ((win[PlatformAPIService.API_NAME] == null) && (win.parent != null) && (win.parent != win)) {
            win = win.parent;
        }
        return win[PlatformAPIService.API_NAME];
    }
    //search either in parent or in opener
    let api;
    // iframes dont have window.opener, in that case try window.parent instead
    if (win.parent != null && win.parent != win) {
        api = scan(win.parent);
    }
    if (api == null && win.opener != null) {
        api = scan(win.opener);
    }
    return api;
}

PlatformAPIService.API_NAME = "platformAPI";